import React, { useState, useEffect } from "react";
import {
  Box, Typography, Grid, Skeleton, Alert, Chip,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import axios from "../../axiosConfig";
import { useTranslation } from "react-i18next";

const DelegatedAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith("ar");

  useEffect(() => {
    axios
      .get("/User/Delegations")
      .then((res) => {
        const data = Array.isArray(res) ? res : (res?.dataList || res?.data || []);
        setAccounts(data);
        setLoading(false);
      })
      .catch(() => {
        setError(isRTL ? "تعذّر تحميل الحسابات المفوّضة" : "Failed to load delegated accounts");
        setLoading(false);
      });
  }, []);

  return (
    <Box dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          pb: 1.5,
          borderBottom: "1px solid #EBEBEB",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PeopleAltOutlinedIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={700} color="primary.main">
            {isRTL ? "الحسابات المفوّضة" : "Delegated Accounts"}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading skeletons */}
      {loading && (
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty state */}
      {!loading && !error && accounts.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: "#BBBBBB",
          }}
        >
          <PeopleAltOutlinedIcon sx={{ fontSize: 56, mb: 1.5, opacity: 0.4 }} />
          <Typography variant="body1" sx={{ color: "#AAAAAA" }}>
            {isRTL ? "لا توجد حسابات مفوّضة" : "No delegated accounts found"}
          </Typography>
        </Box>
      )}

      {/* Account cards */}
      {!loading && accounts.length > 0 && (
        <Grid container spacing={2}>
          {accounts.map((acc) => {
            const isCompany = acc.type === 1;  // 3 = citizen, 1 = company
            return (
              <Grid item xs={12} sm={6} md={4} key={acc.delegateId}>
                <Box
                  sx={{
                    border: "1px solid #E8ECF0",
                    borderRadius: 2.5,
                    p: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    bgcolor: "#FAFBFC",
                    transition: "box-shadow 0.15s",
                    "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,0.08)" },
                  }}
                >
                  {/* Avatar + name + type badge */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        bgcolor: isCompany ? "#1565C0" : "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {isCompany
                        ? <BusinessOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
                        : <PersonOutlineIcon sx={{ color: "#fff", fontSize: 22 }} />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: "#333", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {acc.fullName || "—"}
                      </Typography>
                      <Chip
                        label={
                          isCompany
                            ? (isRTL ? "شركة" : "Company")
                            : (isRTL ? "مواطن" : "Citizen")
                        }
                        size="small"
                        sx={{
                          mt: 0.5,
                          height: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          bgcolor: isCompany ? "#E3F2FD" : "#EDF7ED",
                          color: isCompany ? "#1565C0" : "#2E7D32",
                          border: "none",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Document number */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "#F0F4FA",
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.75,
                    }}
                  >
                    <BadgeOutlinedIcon sx={{ fontSize: 16, color: "#888" }} />
                    <Typography variant="body2" sx={{ color: "#555", fontSize: 13 }}>
                      {isRTL ? "رقم التسجيل:" : "Registration No:"}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: "#333", fontSize: 13, letterSpacing: 0.5 }}
                    >
                      {acc.delegateNum || "—"}
                    </Typography>
                  </Box>

                  {/* Subscriber number (only when present) */}
                  {acc.subscriberNumber != null && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: "#F0F4FA",
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 0.75,
                      }}
                    >
                      <TagOutlinedIcon sx={{ fontSize: 16, color: "#888" }} />
                      <Typography variant="body2" sx={{ color: "#555", fontSize: 13 }}>
                        {isRTL ? "رقم المشترك:" : "Subscriber No:"}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: "#333", fontSize: 13, letterSpacing: 0.5 }}
                      >
                        {acc.subscriberNumber}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default DelegatedAccounts;
